import React, { useRef, useState } from "react";
import { Modal, Box, IconButton, Typography } from "@mui/material";
import {
  ArrowBackIosNewRounded,
  ArrowForwardIosRounded,
  Close,
  PlayArrow,
} from "@mui/icons-material";
import vr from "../assets/vr.jpg";

interface Props {
  selectedPhoto: string | null;
  selectedVideo: string | null;
  setSelectedPhoto: (value: string | null) => void;
  setSelectedVideo: (value: string | null) => void;
  allMedia: { type: "image" | "video"; url: string }[];
}

const MediaPreViewModal: React.FC<Props> = ({
  selectedPhoto,
  selectedVideo,
  setSelectedPhoto,
  setSelectedVideo,
  allMedia,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showReplayButton, setShowReplayButton] = useState(false);
  const getCurrentIndex = () => {
    return selectedPhoto
      ? allMedia.findIndex((m) => m.url === selectedPhoto)
      : selectedVideo
        ? allMedia.findIndex((m) => m.url === selectedVideo)
        : -1;
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = getCurrentIndex();
    if (allMedia.length === 0) return;

    const prevIndex = currentIndex > 0 ? currentIndex - 1 : allMedia.length - 1;
    const prev = allMedia[prevIndex];

    setShowReplayButton(false);
    if (prev.type === "image") {
      setSelectedPhoto(prev.url);
      setSelectedVideo(null);
    } else {
      setSelectedVideo(prev.url);
      setSelectedPhoto(null);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = getCurrentIndex();
    if (allMedia.length === 0) return;

    const nextIndex = currentIndex < allMedia.length - 1 ? currentIndex + 1 : 0;
    const next = allMedia[nextIndex];

    setShowReplayButton(false);
    if (next.type === "image") {
      setSelectedPhoto(next.url);
      setSelectedVideo(null);
    } else {
      setSelectedVideo(next.url);
      setSelectedPhoto(null);
    }
  };

  const handleClose = () => {
    setSelectedPhoto(null);
    setSelectedVideo(null);
    setShowReplayButton(false);
  };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setShowReplayButton(false);
    }
  };

  const handleVideoEnded = () => {
    setShowReplayButton(true);
  };

  return (
    <Modal
      open={!!selectedPhoto || !!selectedVideo}
      onClose={handleClose}
      sx={{ zIndex: 2300, backdropFilter: "blur(15px)" }}
    >
      <Box>
        <IconButton
          onClick={handleClose}
          size="medium"
          sx={{
            position: "absolute",
            top: "2%",
            right: "2.5%",
            color: "white",
            zIndex: 1000,
            backgroundColor: "#00000066",
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            height: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {(selectedPhoto || selectedVideo) && (
            <>
              <IconButton
                onClick={handlePrev}
                size="small"
                sx={{
                  position: "absolute",
                  left: "1.5%",
                  top: "50%",
                  backgroundColor: "#FFFFFF",
                  "&:hover, &:active": { backgroundColor: "#FFFFFF" },
                  zIndex: 1000,
                }}
              >
                <ArrowBackIosNewRounded
                  sx={{ color: "black" }}
                  fontSize="small"
                />
              </IconButton>

              <IconButton
                onClick={handleNext}
                size="small"
                sx={{
                  position: "absolute",
                  right: "1.5%",
                  top: "50%",
                  zIndex: 1000,
                  backgroundColor: "#FFFFFF",
                  "&:hover, &:active": { backgroundColor: "#FFFFFF" },
                }}
              >
                <ArrowForwardIosRounded
                  sx={{ color: "black" }}
                  fontSize="small"
                />
              </IconButton>
            </>
          )}

          <Box position="relative" sx={{ width: "100%", height: "auto" }}>
            {selectedVideo ? (
              <>
                <video
                  ref={videoRef}
                  src={selectedVideo}
                  controls={false}
                  autoPlay
                  playsInline
                  onEnded={handleVideoEnded}
                  style={{
                    width: "100%",
                    margin: "auto",
                    background: "#000",
                    pointerEvents: "none",
                  }}
                />
                {showReplayButton && (
                  <IconButton
                    onClick={handleReplay}
                    size="large"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "white",
                      zIndex: 1001,
                    }}
                  >
                    <PlayArrow style={{ fontSize: 60 }} />
                  </IconButton>
                )}
              </>
            ) : selectedPhoto ? (
              <img
                src={selectedPhoto}
                alt="preview"
                style={{
                  width: "100%",
                  height: "90%",
                  margin: "auto",
                  objectFit: "contain",
                }}
                onError={(e) => (e.currentTarget.src = vr)}
              />
            ) : null}

            <Typography
              variant="caption"
              color="white"
              sx={{
                position: "absolute",
                fontSize: 12,
                bottom: 16,
                right: 16,
                backgroundColor: "rgba(0,0,0,0.5)",
                borderRadius: 1,
                p: 0.5,
              }}
            >
              {getCurrentIndex() + 1} / {allMedia.length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default MediaPreViewModal;
